import { TextLibraryEntry } from '../types';

// letters NFD does not decompose into a base letter + accent
const FOLDED_LETTERS: { [letter: string]: string } = { ø: 'o', æ: 'ae', œ: 'oe', ß: 'ss', đ: 'd', ł: 'l' };

function normalize(value: string) {
  return value
    .toLowerCase()
    .replace(/[øæœßđł]/g, (letter) => FOLDED_LETTERS[letter])
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
}

interface SearchableEntry {
  entry: TextLibraryEntry;
  number: string;
  title: string;
}

// normalised once per library (the store keeps the same array until the host sends a new one), not per keystroke
const searchableCache = new WeakMap<TextLibraryEntry[], SearchableEntry[]>();

function getSearchableEntries(entries: TextLibraryEntry[]) {
  let searchable = searchableCache.get(entries);
  if (!searchable) {
    searchable = entries.map((entry) => ({
      entry,
      number: entry.number ? normalize(entry.number) : '',
      title: normalize(entry.title),
    }));
    searchableCache.set(entries, searchable);
  }
  return searchable;
}

/**
 * Matches by number or title. Entries whose number starts with the query (so "12" finds 12 and 123)
 * come first; then entries whose title contains every word of the query. Case and accents are
 * ignored (e.g. "a" also finds "å", "o" finds "ø").
 */
function filterTextLibraryEntries(entries: TextLibraryEntry[], query: string): TextLibraryEntry[] {
  const normalizedQuery = normalize(query);
  if (!normalizedQuery) {
    return entries;
  }

  const words = normalizedQuery.split(/\s+/);
  const numberMatches: TextLibraryEntry[] = [];
  const titleMatches: TextLibraryEntry[] = [];
  getSearchableEntries(entries).forEach(({ entry, number, title }) => {
    if (number && number.startsWith(normalizedQuery)) {
      numberMatches.push(entry);
      return;
    }
    if (words.every((word) => title.includes(word))) {
      titleMatches.push(entry);
    }
  });
  return [...numberMatches, ...titleMatches];
}

export default filterTextLibraryEntries;
